import { action, observable } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { validateWhileEditing } from '@/features/legends/editor/validation'
import type {
  ConjunctionOperator,
  ConjunctionRule,
} from '@/features/legends/filters/rules'
import {
  FilterEditorViewModelBase,
  type FilterEditorViewModelOptions,
} from '@/features/legends/filters/FilterEditorViewModelBase'
import { FiltersBuilderViewModel } from '@/features/legends/filters/FiltersBuilderViewModel'
import { validateObject } from '@/utils/validx-util'

/**
 * Passes when all (or any) of its sub-filters pass.
 */
export class ConjunctionEditorViewModel extends FilterEditorViewModelBase<ConjunctionRule> {
  private readonly _schema: IValidationSchema<ConjunctionEditorViewModel> = {
    rules: [validateObject('Some filters are invalid.')],
  }

  readonly type = 'Conjunction'

  /**
   * The sub-filters.
   */
  readonly rules: FiltersBuilderViewModel

  @observable accessor op: ConjunctionOperator = 'All'

  readonly validation = validationContext(this, this._schema)

  constructor(options: FilterEditorViewModelOptions) {
    super(options)
    this.rules = new FiltersBuilderViewModel(this._filterOptions)
    this.lifecycle.manage(this.rules)
    this.lifecycle.manage(validateWhileEditing(this, this._schema))
  }

  /**
   * Sets whether all or any of the sub-filters must pass.
   */
  @action.bound
  setOp(op: ConjunctionOperator) {
    this.op = op
  }

  /** @inheritDoc */
  @action
  hydrate({ rules, op }: ConjunctionRule) {
    this.rules.hydrate(rules)
    if (op) {
      this.op = op
    }
  }

  /** @inheritDoc */
  toJSON(): ConjunctionRule {
    return { type: this.type, op: this.op, rules: this.rules.toJSON() }
  }

  /** @inheritDoc */
  receiveErrors(descriptor: ErrorDescriptor) {
    const inner = descriptor.inner ?? {}
    receiveErrors(this, { rules: inner.rules, op: inner.op })
  }
}
